package fr.manyverse.app.infrastructure.repository

import fr.manyverse.app.AppLogger
import fr.manyverse.app.domain.model.ActiveDigicode
import fr.manyverse.app.domain.model.DigicodeAttempt
import fr.manyverse.app.domain.model.DigicodeStatus
import fr.manyverse.app.domain.repository.DigicodeRepository
import fr.manyverse.app.infrastructure.error.logInfrastructureFailure
import fr.manyverse.app.infrastructure.error.mapToDomainException
import fr.manyverse.app.infrastructure.realtime.GameChannelEvent
import fr.manyverse.app.infrastructure.realtime.GameRealtimeChannel
import fr.manyverse.app.infrastructure.serialization.appJson
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Columns
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.channelFlow
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlin.time.Clock
import kotlin.time.Instant

private const val TAG = "DigicodeRepository"
private const val DIGICODES_TABLE = "game_table_digicodes"

/**
 * Supabase implementation of DigicodeRepository.
 */
class SupabaseDigicodeRepository(
    private val supabaseClient: SupabaseClient,
    private val gameChannel: GameRealtimeChannel
) : DigicodeRepository {

    private val json = appJson {
        ignoreUnknownKeys = true
        encodeDefaults = true
    }

    override suspend fun createDigicode(
        tableId: String,
        targetParticipantId: String,
        digitCount: Int,
        maxAttempts: Int,
        timeLimitSeconds: Int?,
        showAttemptsToPlayer: Boolean,
        soundEnabled: Boolean,
        solution: String,
        createdBy: String
    ): Result<ActiveDigicode> {
        return try {
            val insertDto = DigicodeInsertDto(
                tableId = tableId,
                targetParticipantId = targetParticipantId,
                digitCount = digitCount,
                maxAttempts = maxAttempts,
                timeLimitSeconds = timeLimitSeconds,
                showAttemptsToPlayer = showAttemptsToPlayer,
                soundEnabled = soundEnabled,
                solution = solution,
                status = "ACTIVE",
                createdBy = createdBy
            )

            val digicode = supabaseClient
                .from(DIGICODES_TABLE)
                .insert(insertDto) {
                    select()
                }
                .decodeSingle<ActiveDigicodeDto>()

            Result.success(digicode.toDomain())
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to create digicode for table=$tableId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun submitAttempt(
        digicodeId: String,
        newAttempts: List<DigicodeAttempt>,
        newStatus: DigicodeStatus
    ): Result<ActiveDigicode> {
        return try {
            val attemptDtos = newAttempts.map { attempt ->
                AttemptUpdateDto(
                    value = attempt.value,
                    isSuccess = attempt.isSuccess,
                    attemptedAt = attempt.attemptedAt.toString()
                )
            }

            val updatedDigicode = supabaseClient
                .from(DIGICODES_TABLE)
                .update({
                    set<List<AttemptUpdateDto>>("attempts", attemptDtos)
                    set("status", newStatus.name)
                }) {
                    filter {
                        eq("id", digicodeId)
                    }
                    select()
                }
                .decodeSingle<ActiveDigicodeDto>()

            Result.success(updatedDigicode.toDomain())
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to submit attempt for digicode=$digicodeId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun cancel(
        digicodeId: String
    ): Result<Unit> {
        return try {
            supabaseClient
                .from(DIGICODES_TABLE)
                .update({
                    set("status", DigicodeStatus.CANCELLED.name)
                }) {
                    filter {
                        eq("id", digicodeId)
                    }
                }
            Result.success(Unit)
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to cancel digicode=$digicodeId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun timeout(
        digicodeId: String
    ): Result<Unit> {
        return try {
            supabaseClient
                .from(DIGICODES_TABLE)
                .update({
                    set("status", DigicodeStatus.TIMEOUT.name)
                }) {
                    filter {
                        eq("id", digicodeId)
                    }
                }
            Result.success(Unit)
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to timeout digicode=$digicodeId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun getActiveDigicodeForPlayer(
        tableId: String,
        playerId: String
    ): Result<ActiveDigicode?> {
        return try {
            val result = supabaseClient
                .from(DIGICODES_TABLE)
                .select(Columns.ALL) {
                    filter {
                        eq("table_id", tableId)
                        eq("target_participant_id", playerId)
                        eq("status", DigicodeStatus.ACTIVE.name)
                    }
                }
                .decodeList<ActiveDigicodeDto>()
                .firstOrNull()

            Result.success(result?.toDomain())
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to get active digicode for player=$playerId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun getActiveDigicodesForTable(tableId: String): Result<List<ActiveDigicode>> {
        return try {
            val results = supabaseClient
                .from(DIGICODES_TABLE)
                .select(Columns.ALL) {
                    filter {
                        eq("table_id", tableId)
                        eq("status", DigicodeStatus.ACTIVE.name)
                    }
                }
                .decodeList<ActiveDigicodeDto>()
            Result.success(results.map { it.toDomain() })
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to get active digicodes for table=$tableId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override fun observeDigicode(
        digicodeId: String
    ): Flow<ActiveDigicode?> = observeDigicodeRow(
        description = "id=$digicodeId",
        load = {
            supabaseClient
                .from(DIGICODES_TABLE)
                .select(Columns.ALL) {
                    filter { eq("id", digicodeId) }
                }
                .decodeList<ActiveDigicodeDto>()
                .firstOrNull()
        },
        concerns = { record -> extractString(record["id"]) == digicodeId }
    )

    override fun observeActiveDigicodeForPlayer(
        tableId: String,
        playerId: String
    ): Flow<ActiveDigicode?> = observeDigicodeRow(
        description = "player=$playerId",
        load = {
            supabaseClient
                .from(DIGICODES_TABLE)
                .select(Columns.ALL) {
                    filter {
                        eq("table_id", tableId)
                        eq("target_participant_id", playerId)
                        eq("status", DigicodeStatus.ACTIVE.name)
                    }
                }
                .decodeList<ActiveDigicodeDto>()
                .firstOrNull()
        },
        concerns = { record ->
            extractString(record["target_participant_id"]) == playerId &&
                extractString(record["table_id"]) == tableId
        }
    )

    /**
     * Suit un digicode : [load] à l'ouverture et après chaque coupure, puis chaque ligne reçue qui
     * le [concerns]. Un premier chargement raté émet `null` ; un rechargement raté garde l'état affiché.
     */
    private fun observeDigicodeRow(
        description: String,
        load: suspend () -> ActiveDigicodeDto?,
        concerns: (Map<String, Any?>) -> Boolean
    ): Flow<ActiveDigicode?> = channelFlow {
        var loaded = false
        gameChannel.events(DIGICODES_TABLE).collect { event ->
            when (event) {
                GameChannelEvent.Ready -> try {
                    send(load()?.toDomain())
                    loaded = true
                } catch (e: CancellationException) {
                    throw e
                } catch (e: Exception) {
                    logInfrastructureFailure(TAG, "Failed to load digicode $description", e)
                    if (!loaded) send(null)
                }

                is GameChannelEvent.Change -> {
                    val record = event.change.record ?: return@collect
                    if (!concerns(record)) return@collect
                    val digicode = try {
                        parseDigicodeFromRecord(record)
                    } catch (e: Exception) {
                        AppLogger.error(TAG, "Failed to parse digicode update for $description", e)
                        return@collect
                    }
                    send(digicode)
                }
            }
        }
    }

    private fun parseDigicodeFromRecord(record: Map<String, Any?>): ActiveDigicode? {
        val id = extractString(record["id"]) ?: return null
        val tableId = extractString(record["table_id"]) ?: return null
        val targetParticipantId = extractString(record["target_participant_id"]) ?: return null
        val digitCount = extractInt(record["digit_count"]) ?: return null
        val maxAttempts = extractInt(record["max_attempts"]) ?: return null
        val timeLimitSeconds = extractInt(record["time_limit_seconds"])
        val showAttemptsToPlayer = extractBoolean(record["show_attempts_to_player"]) ?: false
        val soundEnabled = extractBoolean(record["sound_enabled"]) ?: true
        val solution = extractString(record["solution"]) ?: return null
        val statusStr = extractString(record["status"]) ?: return null
        val status = try {
            DigicodeStatus.valueOf(statusStr)
        } catch (_: Exception) {
            return null
        }
        val attempts = extractAttempts(record["attempts"])
        val createdBy = extractString(record["created_by"]) ?: return null
        val createdAtStr = extractString(record["created_at"]) ?: return null
        val createdAt = try {
            Instant.parse(createdAtStr)
        } catch (_: Exception) {
            Clock.System.now()
        }

        return ActiveDigicode(
            id = id,
            tableId = tableId,
            targetParticipantId = targetParticipantId,
            digitCount = digitCount,
            maxAttempts = maxAttempts,
            timeLimitSeconds = timeLimitSeconds,
            showAttemptsToPlayer = showAttemptsToPlayer,
            soundEnabled = soundEnabled,
            solution = solution,
            status = status,
            attempts = attempts,
            createdBy = createdBy,
            createdAt = createdAt
        )
    }

    private fun extractAttempts(value: Any?): List<DigicodeAttempt> {
        return when (value) {
            null -> emptyList()
            is JsonArray -> value.mapNotNull { parseAttemptFromJsonElement(it) }
            is JsonPrimitive -> {
                try {
                    json.parseToJsonElement(value.content).let { element ->
                        if (element is JsonArray) {
                            element.mapNotNull { parseAttemptFromJsonElement(it) }
                        } else emptyList()
                    }
                } catch (_: Exception) {
                    emptyList()
                }
            }
            is String -> {
                try {
                    val element = json.parseToJsonElement(value)
                    if (element is JsonArray) {
                        element.mapNotNull { parseAttemptFromJsonElement(it) }
                    } else emptyList()
                } catch (_: Exception) {
                    emptyList()
                }
            }
            is List<*> -> value.mapNotNull { item ->
                if (item is Map<*, *>) {
                    val v = item["value"] as? String ?: return@mapNotNull null
                    val isSuccess = item["is_success"] as? Boolean ?: false
                    val attemptedAtStr = item["attempted_at"] as? String ?: return@mapNotNull null
                    val attemptedAt = try {
                        Instant.parse(attemptedAtStr)
                    } catch (_: Exception) {
                        Clock.System.now()
                    }
                    DigicodeAttempt(value = v, isSuccess = isSuccess, attemptedAt = attemptedAt)
                } else null
            }
            else -> emptyList()
        }
    }

    private fun parseAttemptFromJsonElement(element: kotlinx.serialization.json.JsonElement): DigicodeAttempt? {
        return try {
            val obj = element.jsonObject
            val value = obj["value"]?.jsonPrimitive?.content ?: return null
            val isSuccess = obj["is_success"]?.jsonPrimitive?.booleanOrNull ?: false
            val attemptedAtStr = obj["attempted_at"]?.jsonPrimitive?.content ?: return null
            val attemptedAt = try {
                Instant.parse(attemptedAtStr)
            } catch (_: Exception) {
                Clock.System.now()
            }
            DigicodeAttempt(value = value, isSuccess = isSuccess, attemptedAt = attemptedAt)
        } catch (_: Exception) {
            null
        }
    }

    private fun extractBoolean(value: Any?): Boolean? = when (value) {
        null -> null
        is Boolean -> value
        is JsonPrimitive -> value.content.toBooleanStrictOrNull()
        is String -> value.toBooleanStrictOrNull()
        else -> null
    }

    private fun extractInt(value: Any?): Int? = when (value) {
        null -> null
        is Number -> value.toInt()
        is JsonPrimitive -> value.content.toIntOrNull()
        is String -> value.toIntOrNull()
        else -> null
    }

    private fun extractString(value: Any?): String? = when (value) {
        null -> null
        is JsonPrimitive -> value.content
        is String -> value
        else -> value.toString()
    }

    // DTO for inserting a digicode
    @Serializable
    private data class DigicodeInsertDto(
        @SerialName("table_id")
        val tableId: String,

        @SerialName("target_participant_id")
        val targetParticipantId: String,

        @SerialName("digit_count")
        val digitCount: Int,

        @SerialName("max_attempts")
        val maxAttempts: Int,

        @SerialName("time_limit_seconds")
        val timeLimitSeconds: Int?,

        @SerialName("show_attempts_to_player")
        val showAttemptsToPlayer: Boolean,

        @SerialName("sound_enabled")
        val soundEnabled: Boolean,

        @SerialName("solution")
        val solution: String,

        @SerialName("status")
        val status: String,

        @SerialName("created_by")
        val createdBy: String
    )

    // DTO for reading a digicode from Supabase queries
    @Serializable
    private data class ActiveDigicodeDto(
        @SerialName("id")
        val id: String,

        @SerialName("table_id")
        val tableId: String,

        @SerialName("target_participant_id")
        val targetParticipantId: String,

        @SerialName("digit_count")
        val digitCount: Int,

        @SerialName("max_attempts")
        val maxAttempts: Int,

        @SerialName("time_limit_seconds")
        val timeLimitSeconds: Int?,

        @SerialName("show_attempts_to_player")
        val showAttemptsToPlayer: Boolean,

        @SerialName("sound_enabled")
        val soundEnabled: Boolean,

        @SerialName("solution")
        val solution: String,

        @SerialName("status")
        val status: String,

        @SerialName("attempts")
        val attempts: List<DigicodeAttemptDto>,

        @SerialName("created_by")
        val createdBy: String,

        @SerialName("created_at")
        val createdAt: Instant
    ) {
        fun toDomain(): ActiveDigicode = ActiveDigicode(
            id = id,
            tableId = tableId,
            targetParticipantId = targetParticipantId,
            digitCount = digitCount,
            maxAttempts = maxAttempts,
            timeLimitSeconds = timeLimitSeconds,
            showAttemptsToPlayer = showAttemptsToPlayer,
            soundEnabled = soundEnabled,
            solution = solution,
            status = DigicodeStatus.valueOf(status),
            attempts = attempts.map { it.toDomain() },
            createdBy = createdBy,
            createdAt = createdAt
        )
    }

    @Serializable
    private data class DigicodeAttemptDto(
        @SerialName("value")
        val value: String,

        @SerialName("is_success")
        val isSuccess: Boolean,

        @SerialName("attempted_at")
        val attemptedAt: Instant
    ) {
        fun toDomain() = DigicodeAttempt(
            value = value,
            isSuccess = isSuccess,
            attemptedAt = attemptedAt
        )
    }

    // DTO for encoding attempts in an update (uses String for Instant)
    @Serializable
    private data class AttemptUpdateDto(
        @SerialName("value")
        val value: String,

        @SerialName("is_success")
        val isSuccess: Boolean,

        @SerialName("attempted_at")
        val attemptedAt: String
    )
}
